import { supabase } from "../../lib/supabase";
import { getUserByAuthId } from "./usersService";
import { getFromCache, setToCache } from "./cacheService";

export const getStores = (callback: (stores: any[]) => void) => {
  const cached = getFromCache('stores');
  if (cached) {
    callback(cached);
  }

  const fetchStores = async () => {
    const { data, error } = await supabase
      .from('stores')
      .select('*, state:states(code, name)')
      .order('name', { ascending: true });
    
    if (error) {
      console.error('Error fetching stores:', error);
      return;
    }

    const mappedData = (data || []).map(s => ({
      ...s,
      estado: s.state?.code || s.estado // Fallback to estado if it still exists or isn't null
    }));

    setToCache('stores', mappedData);
    callback(mappedData);
  };

  fetchStores();

  const channelName = `public:stores:${Math.random().toString(36).substring(2)}`;
  const subscription = supabase
    .channel(channelName)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'stores' }, fetchStores)
    .subscribe();

  return () => {
    supabase.removeChannel(subscription);
  };
};


export const getMyStore = async () => {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return null;

        const profile = await getUserByAuthId(session.user.id);
        if (!profile) return null;
        
        const { data, error } = await supabase
          .from('stores')
          .select('*, store_members!inner(user_id), state:states(code, name)')
          .eq('store_members.user_id', profile.id)
          .maybeSingle();

        if (error) throw error;
        
        if (data) {
          return {
            ...data,
            estado: data.state?.code || data.estado
          };
        }

        if (profile.store_id) {
          const { data: storeByProfile } = await supabase
            .from('stores')
            .select('*, state:states(code, name)')
            .eq('id', profile.store_id)
            .maybeSingle();
          if (storeByProfile) {
            return {
              ...storeByProfile,
              estado: storeByProfile.state?.code || storeByProfile.estado
            };
          }
        }

        return null;
    } catch (error) {
        console.error('Error fetching my store:', error);
        return null;
    }
};


export const getStoreMPAccount = async (storeId: any) => {
    try {
        const { data, error } = await supabase
            .from('store_mp_accounts')
            .select('id, store_id, mp_user_id, created_at, access_token')
            .eq('store_id', Number(storeId))
            .maybeSingle();
        if (error) throw error;
        if (data) {
            return {
                ...data,
                connected: !!data.access_token
            };
        }
        return data;
    } catch (err) {
        console.error('Error fetching Store MP Account:', err);
        return null;
    }
};


export const disconnectStoreMPAccount = async (storeId: any) => {
    try {
        const response = await fetch(`/api/store/${Number(storeId)}/mp-account`, {
            method: 'DELETE'
        });
        if (!response.ok) {
            throw new Error(`HTTP error ${response.status}`);
        }
        const data = await response.json();
        if (data && data.success) {
            return true;
        }
        return false;
    } catch (err) {
        console.error('Error disconnecting Store MP Account via backend proxy:', err);
        return false;
    }
};


export const updateStoreInfo = async (storeId: any, updates: any) => {
    try {
        const dataToUpdate = { ...updates };
        
        // If 'estado' is passed, but 'state_id' is not, resolve it mapped to code:
        if (updates.estado && !updates.state_id) {
            const stateCode = String(updates.estado).trim().toUpperCase();
            const { data: stateData, error: stateError } = await supabase
                .from('states')
                .select('id')
                .eq('code', stateCode)
                .maybeSingle();

            if (!stateError && stateData) {
                dataToUpdate.state_id = stateData.id;
            }
        }
        
        // Always remove non-existent 'estado' column to prevent DB update errors
        delete dataToUpdate.estado;

        const { error } = await supabase
          .from('stores')
          .update(dataToUpdate)
          .eq('id', storeId);
        
        if (error) throw error;
        return true;
    } catch (error) {
        console.error('Error updating store info:', error);
        return false;
    }
};


export const updateStoreSalesStatus = async (storeId: number | string, salesEnabled: boolean) => {
    try {
        const { error } = await supabase
          .from('stores')
          .update({
            sales_enabled: salesEnabled,
            updated_at: new Date().toISOString()
          })
          .eq('id', Number(storeId));
        
        if (error) throw error;
        return true;
    } catch (error) {
        console.error('Error updating store sales status:', error);
        return false;
    }
};


export const getStoreGames = async (storeId: number | string) => {
    try {
        const { data, error } = await supabase
          .from('store_games')
          .select('game_id')
          .eq('store_id', storeId);
        
        if (error) throw error;
        return data ? data.map((item: any) => item.game_id) : [];
    } catch (error) {
        console.error('Error fetching store games:', error);
        return [];
    }
};


export const updateStoreGames = async (storeId: number | string, gameIds: (number | string)[]) => {
    try {
        // 1. Delete existing connections for this store
        const { error: deleteError } = await supabase
          .from('store_games')
          .delete()
          .eq('store_id', storeId);
        
        if (deleteError) throw deleteError;

        // 2. Insert new connections
        if (gameIds.length > 0) {
            const payload = gameIds.map(id => ({
                store_id: typeof storeId === 'string' ? parseInt(storeId) : storeId,
                game_id: typeof id === 'string' ? parseInt(id) : id
            }));
            
            const { error: insertError } = await supabase
              .from('store_games')
              .insert(payload);
            
            if (insertError) throw insertError;
        }
        return true;
    } catch (error) {
        console.error('Error updating store games:', error);
        return false;
    }
};


export const addStoreSchedule = async (params: any) => {
    try {
        const payload = {
            store_id: params.store_id,
            weekday: params.dia,
            entry_time: params.horario,
            entry_tax: params.valor_insc !== undefined && params.valor_insc !== '' ? parseFloat(params.valor_insc) : null,
            note: params.observacao || null,
            game_id: params.game_id ? parseInt(params.game_id) : null
        };
        const { error } = await supabase
          .from('store_schedule')
          .insert(payload);
        
        if (error) throw error;
        return true;
    } catch (error) {
        console.error('Error adding store schedule:', error);
        return false;
    }
};


export const updateStoreSchedule = async (id: number | string, params: any) => {
    try {
        const payload = {
            weekday: params.dia,
            entry_time: params.horario,
            entry_tax: params.valor_insc !== undefined && params.valor_insc !== '' ? parseFloat(params.valor_insc) : null,
            note: params.observacao || null,
            game_id: params.game_id ? parseInt(params.game_id) : null
        };
        const { error } = await supabase
          .from('store_schedule')
          .update(payload)
          .eq('id', id);
        
        if (error) throw error;
        return true;
    } catch (error) {
        console.error('Error updating store schedule:', error);
        return false;
    }
};


export const deleteStoreSchedule = async (id: number | string) => {
    try {
        const { error } = await supabase
          .from('store_schedule')
          .delete()
          .eq('id', id);
        
        if (error) throw error;
        return true;
    } catch (error) {
        console.error('Error deleting store schedule:', error);
        return false;
    }
};


export const getStoreBySlug = async (slug: string) => {
  try {
    const isNumeric = !isNaN(Number(slug));
    let query = supabase.from('stores').select('*, state:states(*)');
    
    if (isNumeric) {
      query = query.or(`slug.eq."${slug}",id.eq.${slug}`);
    } else {
      query = query.eq('slug', slug);
    }
    
    const { data, error } = await query.limit(1);
    
    if (error) throw error;
    
    if (data && data.length > 0) {
      const s = data[0];
      return {
        ...s,
        estado: s.state?.code || s.estado
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching store by slug:', error);
    return null;
  }
};


export const getStoreProfileInfo = async (username: string) => {
  try {
    const { data: user, error: userError } = await supabase
      .from('users')
      .select('*, store_members(store:stores(*, state:states(code, name)))')
      .eq('username', username)
      .maybeSingle();

    if (userError) throw userError;
    if (!user) return null;

    let storeData = user.store_members && user.store_members.length > 0 
      ? user.store_members[0].store 
      : null;

    if (storeData) {
      storeData = {
        ...storeData,
        estado: storeData.state?.code || storeData.estado
      };
    }

    return {
      user: { ...user, store_members: undefined },
      store: storeData
    };
  } catch (error) {
    console.error('Error fetching store profile info:', error);
    return null;
  }
};


export const getStoreSchedule = async (storeId: string | number) => {
  try {
    const { data, error } = await supabase
      .from('store_schedule')
      .select('*, games!fk_store_schedule_game(name)')
      .eq('store_id', storeId);
    
    if (error) throw error;
    return (data || []).map(item => ({
      ...item,
      dia: item.weekday || '',
      horario: item.entry_time ? item.entry_time.substring(0, 5) : '',
      valor_insc: item.entry_tax !== null && item.entry_tax !== undefined ? String(item.entry_tax) : '0',
      observacao: item.note || '',
      game_name: item.games?.name || item.jogo || ''
    }));
  } catch (error) {
    console.error('Error fetching store schedule:', error);
    return [];
  }
};


export const getStoreHours = async (storeId: string | number) => {
  try {
    const { data, error } = await supabase
      .from('store_hours')
      .select('*')
      .eq('store_id', storeId);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching store hours:', error);
    return [];
  }
};


export const updateStoreHours = async (storeId: string | number, hours: any[]) => {
  try {
    const { error: deleteError } = await supabase
      .from('store_hours')
      .delete()
      .eq('store_id', storeId);
      
    if (deleteError) throw deleteError;
    
    if (hours.length > 0) {
      const { error: insertError } = await supabase
        .from('store_hours')
        .insert(hours.map(h => ({
          store_id: storeId,
          day_of_week: h.day_of_week,
          open_time: h.open_time || null,
          close_time: h.close_time || null,
          is_closed: h.is_closed !== undefined ? h.is_closed : false
        })));
        
      if (insertError) throw insertError;
    }
    
    return true;
  } catch (error) {
    console.error('Error updating store hours:', error);
    return false;
  }
};


export const updateStoreStock = async (
  store_id: string | number,
  product_id: number | string,
  quantity: number,
  price?: number,
  pre_sale?: boolean,
  max_per_user?: number | null
) => {
  try {
    // Garante valor mínimo de R$ 0,05 (5 centavos) para produtos no estoque
    const finalPrice = price !== undefined && price !== null ? Math.max(0.05, Number(price)) : 0.05;

    // Quantidade alvo do estoque
    const targetQuantity = quantity;

    const payload: any = {
      store_id,
      product_id,
      quantity: targetQuantity,
      store_price: finalPrice,
      pre_sale,
      deleted_at: null // Resgata/reativa o produto se ele estava excluído logicamente (soft delete)
    };

    if (max_per_user !== undefined) {
      payload.max_per_user = max_per_user !== null ? Math.max(1, Number(max_per_user)) : null;
    }

    const { data, error } = await supabase
      .from('store_products')
      .upsert(payload, { onConflict: 'store_id,product_id' })
      .select();
    
    if (error) throw error;
    return data;
  } catch (error: any) {
    console.error('Error updating store stock:', error);
    return { error: error.message };
  }
};


export const softDeleteStoreStock = async (store_id: string | number, product_id: number | string) => {
  try {
    const { data, error } = await supabase
      .from('store_products')
      .update({
        deleted_at: new Date().toISOString()
      })
      .eq('store_id', store_id)
      .eq('product_id', product_id)
      .select();
    
    if (error) throw error;
    return data;
  } catch (error: any) {
    console.error('Error soft deleting store stock:', error);
    return { error: error.message };
  }
};


export const getStoreEvents = async (storeId: string) => {
  try {
    // 1. Try querying by direct store_id first (highly efficient new schema support)
    const { data, error } = await supabase
      .from('tournaments')
      .select('*, games:games!tournaments_cardgame_id_fkey(name)')
      .eq('store_id', storeId);
    
    if (!error && data && data.length > 0) {
      return data.map((t: any) => ({ ...t, start_date: t.starts_at || t.start_date }));
    }

    // 2. Fallback to owner's created tournaments if store_id matches nothing or error (legacy support)
    const { data: member } = await supabase
      .from('store_members')
      .select('user_id')
      .eq('store_id', storeId)
      .limit(1)
      .maybeSingle();

    if (!member) return (data || []).map((t: any) => ({ ...t, start_date: t.starts_at || t.start_date }));

    const { data: legacyData, error: legacyError } = await supabase
      .from('tournaments')
      .select('*, games:games!tournaments_cardgame_id_fkey(name)')
      .eq('created_by', member.user_id);
    
    if (legacyError) {
      console.error('Error fetching store tournaments legacy:', legacyError);
      return (data || []).map((t: any) => ({ ...t, start_date: t.starts_at || t.start_date }));
    }
    return (legacyData || []).map((t: any) => ({ ...t, start_date: t.starts_at || t.start_date }));
  } catch (error) {
    console.error('Error fetching store events:', error);
    return [];
  }
};


export const getStoreTournaments = async (username: string) => {
  try {
    const { data: user } = await supabase.from('users').select('id').eq('username', username).maybeSingle();
    if (!user) return [];

    const { data: store } = await supabase
      .from('stores')
      .select('id, store_members!inner(user_id)')
      .eq('store_members.user_id', user.id)
      .maybeSingle();
    
    if (!store) return [];

    const { data, error } = await supabase
      .from('tournaments')
      .select('*, games!tournaments_cardgame_id_fkey(name), game_formats:game_formats!tournaments_format_id_fkey(name)')
      .eq('created_by', user.id);

    if (error) throw error;
    
    // Map starts_at to start_date for compatibility
    return (data || []).map((t: any) => ({
      ...t,
      game_formats: t.game_formats || t.tournament_formats,
      tournament_formats: t.game_formats || t.tournament_formats,
      start_date: t.starts_at || t.start_date
    }));
  } catch (error: any) {
    console.error('Error fetching store tournaments:', error);
    return [];
  }
};


export const getStoreShippingMethods = async (storeId: string | number) => {
    try {
        const { data: rpcData, error: rpcError } = await supabase.rpc('get_store_shipping_options', { p_store_id: Number(storeId) });
        if (rpcError) throw rpcError;
        
        const mappedRpc = (rpcData || []).map((item: any) => ({
            id: item.shipping_method_id,
            shipping_method_id: item.shipping_method_id,
            name: item.name,
            service_type: item.service_type,
            company_name: item.company_name,
            price: Number(item.price ?? 0),
            estimated_days: Number(item.delivery_days ?? 0),
            delivery_days: Number(item.delivery_days ?? 0),
            source: item.source,
            external_id: item.external_id,
            is_better_envio: item.is_better_envio
        }));

        let tableName = 'store_shipping_methods';
        let { error: checkError } = await supabase.from(tableName).select('count', { count: 'exact', head: true }).limit(1);
        if (checkError) {
          tableName = 'store_shipping_options';
        }

        const { data: storeSM } = await supabase
          .from(tableName)
          .select('*')
          .eq('store_id', Number(storeId))
          .eq('is_active', true);

        const { data: shipM } = await supabase
          .from('shipping_methods')
          .select('*')
          .eq('must_calculate', false)
          .eq('is_active', true);

        const manualMap: any[] = [];
        if (storeSM && shipM) {
          storeSM.forEach((sm: any) => {
            const method = shipM.find((m: any) => m.id === sm.shipping_method_id || m.id === sm.shipping_option_id);
            if (method) {
              const smId = sm.shipping_method_id || sm.shipping_option_id;
              manualMap.push({
                id: smId,
                shipping_method_id: smId,
                name: method.name,
                service_type: method.service_type,
                company_name: method.company_name || 'Transportadora',
                price: Number(sm.price_override !== null && sm.price_override !== undefined ? sm.price_override : (method.avg_price ?? 0)),
                estimated_days: Number(sm.delivery_days_override !== null && sm.delivery_days_override !== undefined ? sm.delivery_days_override : (method.delivery_days ?? 0)),
                delivery_days: Number(sm.delivery_days_override !== null && sm.delivery_days_override !== undefined ? sm.delivery_days_override : (method.delivery_days ?? 0)),
                source: method.source || 'manual',
                external_id: method.external_id,
                is_better_envio: false
              });
            }
          });
        }

        const finalMethods = [...mappedRpc];
        manualMap.forEach(manual => {
          const index = finalMethods.findIndex(fm => fm.shipping_method_id === manual.shipping_method_id);
          if (index !== -1) {
            finalMethods[index] = manual;
          } else {
            finalMethods.push(manual);
          }
        });

        return finalMethods;
    } catch (error) {
        console.error('Error fetching shipping methods:', error);
        return [];
    }
};


export const getStorePaymentMethods = async (storeId: string | number) => {
    try {
        const { data, error } = await supabase
            .from('store_payment_methods')
            .select('*, payment_methods(*)')
            .eq('store_id', storeId)
            .eq('enabled', true);
            
        if (error) throw error;
        // Flatten the response
        return (data || []).map(item => ({
            ...item.payment_methods,
            ...item,
            id: item.payment_method_id,
            original_id: item.id
        }));
    } catch (error) {
        console.error('Error fetching payment methods:', error);
        return [];
    }
};


export const isStoreOpen = async (storeId: string | number): Promise<boolean> => {
    try {
        const { data, error } = await supabase.rpc('is_store_open', {
            p_store_id: Number(storeId)
        });
        if (error) throw error;
        return data === true;
    } catch (error) {
        console.error('Error checking if store is open:', error);
        return false;
    }
};


export const getStoreShippingMethodsConfig = async (storeId: number | string) => {
  try {
    // Check which join table we should use (store_shipping_methods or store_shipping_options)
    let tableName = 'store_shipping_methods';
    let { error: checkError } = await supabase.from(tableName).select('count', { count: 'exact', head: true }).limit(1);
    
    if (checkError) {
      tableName = 'store_shipping_options';
    }

    const { data, error } = await supabase
      .from(tableName)
      .select('*')
      .eq('store_id', Number(storeId));
      
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching store shipping methods configuration:', error);
    return [];
  }
};


export const updateStoreShippingMethods = async (storeId: number | string, selectedMethodIds: number[]) => {
  try {
    let tableName = 'store_shipping_methods';
    let { error: checkError } = await supabase.from(tableName).select('count', { count: 'exact', head: true }).limit(1);
    
    if (checkError) {
      tableName = 'store_shipping_options';
    }

    // Delete existing
    await supabase
      .from(tableName)
      .delete()
      .eq('store_id', Number(storeId));

    if (selectedMethodIds.length === 0) return true;

    // Try inserting with 'enabled' column first
    const insertData = selectedMethodIds.map(id => ({
      store_id: Number(storeId),
      shipping_method_id: id,
      enabled: true
    }));

    const { error: insertError } = await supabase
      .from(tableName)
      .insert(insertData);

    if (insertError) {
      // If fails (usually because 'enabled' column is missing or constraint is different), try without it
      const insertDataNoEnabled = selectedMethodIds.map(id => ({
        store_id: Number(storeId),
        shipping_method_id: id
      }));
      const { error: insertError2 } = await supabase
        .from(tableName)
        .insert(insertDataNoEnabled);
      if (insertError2) throw insertError2;
    }

    return true;
  } catch (error) {
    console.error('Error updating store shipping methods:', error);
    return false;
  }
};


export const updateStorePaymentMethods = async (storeId: number | string, selectedMethodIds: number[]) => {
  try {
    // Delete existing
    await supabase
      .from('store_payment_methods')
      .delete()
      .eq('store_id', Number(storeId));

    if (selectedMethodIds.length === 0) return true;

    const insertData = selectedMethodIds.map(id => ({
      store_id: Number(storeId),
      payment_method_id: id,
      enabled: true
    }));

    const { error } = await supabase
      .from('store_payment_methods')
      .insert(insertData);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating store payment methods:', error);
    return false;
  }
};


export const getMyStorePaymentConfigs = async (storeId: number | string) => {
  try {
    const { data, error } = await supabase
      .from('store_payment_methods')
      .select('payment_method_id')
      .eq('store_id', Number(storeId))
      .eq('enabled', true);
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
};


